import { createContext, ReactNode, useContext } from 'react';

import styles from './Highlight.module.scss';

// The active global-search query, shared with renderers so matches highlight inside
// their text without threading the query through the frozen CellRenderer contract.
export const SearchContext = createContext<string>('');

export const useSearchQuery = () => useContext(SearchContext);

interface HighlightProps {
  text: string;
}

// Renders escaped `text` (no HTML tags), wrapping case-insensitive matches of the current
// query in a <mark>. Never uses dangerouslySetInnerHTML.
export const Highlight = ({ text }: HighlightProps) => {
  const query = useSearchQuery().trim();
  if (!query) return <span className={styles.wrap}>{text}</span>;

  const lower = text.toLowerCase();
  const needle = query.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  let match = lower.indexOf(needle);

  while (match !== -1) {
    if (match > cursor) parts.push(<span key={`t${cursor}`}>{text.slice(cursor, match)}</span>);
    parts.push(
      <mark
        key={`m${match}`}
        className={styles.hl}
      >
        {text.slice(match, match + needle.length)}
      </mark>,
    );
    cursor = match + needle.length;
    match = lower.indexOf(needle, cursor);
  }
  if (cursor < text.length) parts.push(<span key={`t${cursor}`}>{text.slice(cursor)}</span>);

  return <span className={styles.wrap}>{parts}</span>;
};
